import { Capacitor } from '@capacitor/core';
import { ConnectionStatus, Network } from '@capacitor/network';
import { logSentry } from 'app/core/errors/error-utils';
import { contextActions } from 'app/core/redux/context-redux';
import { useObservableEagerState } from 'observable-hooks';
import React, { useEffect, useState } from 'react';
import { Detector } from 'react-detect-offline';
import { useDispatch, useSelector } from 'react-redux';
import { BehaviorSubject } from 'rxjs';

type Props = {
  online: boolean;
};

const delayBeforeShowingOffline = 3000;
const delayBeforeHidingBackOnline = 4000;

const capNetworkStatus$ = new BehaviorSubject<ConnectionStatus>({
  connected: false,
  connectionType: 'unknown',
});
Network.getStatus()
  .then(status => capNetworkStatus$.next(status))
  .catch(logSentry);

Network.removeAllListeners().catch(logSentry);
Network.addListener('networkStatusChange', status =>
  capNetworkStatus$.next(status)
).catch(logSentry);

function NetworkMonitorInner({ online: webOnline }: Props): null {
  const capOnline =
    useObservableEagerState(capNetworkStatus$).connected;

  const online = Capacitor.isNativePlatform() ? capOnline : webOnline;

  const dispatch = useDispatch();
  const reduxAppOnlineState = useSelector(s => s.context.appOnline);

  const [backOnlineTimeout, setBackOnlineTimeout] =
    useState<NodeJS.Timeout>();
  const [offlineTimeout, setOfflineTimeout] =
    useState<NodeJS.Timeout>();

  useEffect(() => {
    if (!online && !offlineTimeout) {
      const timeout = setTimeout(() => {
        dispatch(contextActions.appOnline.set(false));
      }, delayBeforeShowingOffline);

      setOfflineTimeout(timeout);
    } else if (online && offlineTimeout) {
      clearTimeout(offlineTimeout);
      setOfflineTimeout(undefined);
    }
  }, [dispatch, online, offlineTimeout]);

  useEffect(() => {
    const backOnline = online && reduxAppOnlineState === false;
    if (backOnline && !backOnlineTimeout) {
      dispatch(contextActions.appOnline.set('backOnline'));

      const timeout = setTimeout(() => {
        dispatch(contextActions.appOnline.set(true));
      }, delayBeforeHidingBackOnline);

      setBackOnlineTimeout(timeout);
    } else if (!online && backOnlineTimeout) {
      clearTimeout(backOnlineTimeout);
      setBackOnlineTimeout(undefined);
    }
  }, [dispatch, online, backOnlineTimeout, reduxAppOnlineState]);

  return null;
}

export default function NetworkMonitor(): JSX.Element {
  return (
    <Detector
      polling={false}
      render={p => <NetworkMonitorInner {...p} />}
    />
  );
}
